// ============================================================
// Icon.jsx – zentrale Icon-Komponente (Linien-Icons, lucide)
// In navConfig & Co. wird nur ein Name gesetzt (z. B. "users");
// <Icon name="users" /> rendert das passende SVG (currentColor →
// nimmt automatisch die Text-/Aktiv-/Akzentfarbe an).
// Unbekannter Name? → Text wird als Text gezeigt, ein Emoji gar nicht
// (Keine-Emojis-Regel, siehe CLAUDE.md).
// ============================================================
import {
  Lightbulb,
  Home, Folder, Users, User, UsersRound, ClipboardList, DoorOpen, FilePen, Bot,
  FileText, Sparkles, ScrollText, CircleCheck, TrendingDown, LayoutDashboard,
  ListChecks, LineChart, Globe, Inbox, KeyRound, Settings, Clapperboard,
  Calendar, CalendarClock, CalendarDays, MapPin, CreditCard, Package, Tag,
  Drama, Music, Music2, Megaphone, Mail, Newspaper, Cake, Gift, Star,
  Handshake, Ticket, Mic, Armchair, Sofa, Euro, Clock, GraduationCap,
  MessageCircle, MessageSquare, Wallet, Receipt, Send, MailWarning, Link2,
  Landmark, ShoppingCart, Box, Plug, Smartphone, Video, BarChart3, QrCode,
  Pencil, SquarePen, Ban, Trash2, Plus, X, Check, AtSign, Phone, Bell,
  Printer, Download, RotateCcw, Calculator, Monitor, BookOpen, Files, Stethoscope, Trophy,
  Palette, Shirt, Wrench, Play, Paperclip, Search, Lock, Image, Moon, Minus,
  Crown, Save, Mouse, Bookmark, FlaskConical, Heart, Target, Camera, Wand2, Sun,
  Hourglass, TreePalm, Pin, Flag, Archive, FolderOpen, TriangleAlert, ChevronRight,
  Share2, ClipboardCheck, Coffee, Hand, ArrowLeft,
  Zap, Disc3, ListMusic, Upload, Headphones, Pause, Square, SkipForward, SkipBack,
  AlertCircle, ArrowRight, ArrowRightLeft, BadgeCheck, CalendarCheck, CalendarX, CheckCheck, CheckCircle, Code, Eye, EyeOff, FilePlus, HelpCircle, History, Info, Layers, Percent, PhoneIncoming, PhoneOutgoing, Repeat, ScanLine, Shield, ShieldAlert, ShieldCheck, Undo, UserPlus,
} from "lucide-react";
import { GEN_MAP, GEN_EMOJI } from "./icon-gen";

// Keine-Emojis-Regel (CLAUDE.md): ein Emoji wird NIE als Text gezeigt.
// Bunt dargestellte Zeichen (Emoji_Presentation) und alles mit U+FE0F.
const EMOJI_RE = /\p{Emoji_Presentation}|\uFE0F/u;
const ohneFE0F = (s) => (typeof s === "string" ? s.replace(/\uFE0F/g, "") : s);

const MAP = {
  "disc": Disc3, "list-music": ListMusic, "upload": Upload, "rotate-ccw": RotateCcw, "headphones": Headphones, "pause": Pause, "square": Square, "skip-forward": SkipForward, "skip-back": SkipBack,
  "clipboard-check": ClipboardCheck, "coffee": Coffee, "hand": Hand, "house": Home,
  "circle-alert": AlertCircle, "chevron-right": ChevronRight, "arrow-left": ArrowLeft,
  "home": Home, "folder": Folder, "users": Users, "user": User, "users-round": UsersRound,
  "clipboard-list": ClipboardList, "door-open": DoorOpen, "file-pen": FilePen,
  "file-text": FileText, "sparkles": Sparkles, "scroll": ScrollText, "circle-check": CircleCheck,
  "trending-down": TrendingDown, "dashboard": LayoutDashboard, "list-checks": ListChecks, "zap": Zap,
  "line-chart": LineChart, "bar-chart": BarChart3, "globe": Globe, "inbox": Inbox,
  "key": KeyRound, "settings": Settings, "clapperboard": Clapperboard, "calendar": Calendar,
  "calendar-clock": CalendarClock, "calendar-days": CalendarDays, "map-pin": MapPin,
  "credit-card": CreditCard, "package": Package, "tag": Tag, "drama": Drama, "music": Music,
  "music-2": Music2, "megaphone": Megaphone, "mail": Mail, "newspaper": Newspaper, "cake": Cake,
  "gift": Gift, "star": Star, "handshake": Handshake, "ticket": Ticket, "mic": Mic,
  "armchair": Armchair, "sofa": Sofa, "euro": Euro, "clock": Clock, "graduation-cap": GraduationCap,
  "message-circle": MessageCircle, "message-square": MessageSquare, "wallet": Wallet,
  "receipt": Receipt, "send": Send, "mail-warning": MailWarning, "link": Link2,
  "landmark": Landmark, "shopping-cart": ShoppingCart, "box": Box, "plug": Plug, "qr-code": QrCode, "tree-palm": TreePalm,
  "smartphone": Smartphone, "video": Video, "instagram": InstagramGlyph, "bot": Bot,
  "edit": SquarePen, "pencil": Pencil, "ban": Ban, "trash": Trash2, "plus": Plus,
  "x": X, "check": Check, "at-sign": AtSign, "phone": Phone, "bell": Bell,
  "printer": Printer, "download": Download, "refresh": RotateCcw, "refresh-cw": RotateCcw,
  "calculator": Calculator, "monitor": Monitor, "book": BookOpen, "book-open": BookOpen, "files": Files,
  "stethoscope": Stethoscope, "trophy": Trophy,
  "palette": Palette, "shirt": Shirt, "wrench": Wrench, "play": Play, "paperclip": Paperclip,
  "search": Search, "lock": Lock, "image": Image, "moon": Moon, "minus": Minus,
  "crown": Crown, "save": Save, "mouse": Mouse, "bookmark": Bookmark, "flask": FlaskConical,
  "lightbulb": Lightbulb, "heart": Heart, "target": Target, "camera": Camera, "wand": Wand2, "sun": Sun,
  "hourglass": Hourglass, "palm": TreePalm, "music-note": Music, "image-frame": Image,
  "pin": Pin, "flag": Flag, "archive": Archive, "folder-open": FolderOpen, "chevron": ChevronRight,
  // Nachgetragen: Symbole der neueren Module (Hilfe, Telefon, Sicherung, Börse)
  "alert-circle": AlertCircle,
  "arrow-right": ArrowRight, "arrow-right-left": ArrowRightLeft,
  "badge-check": BadgeCheck,
  "calendar-check": CalendarCheck,
  "calendar-x": CalendarX,
  "check-check": CheckCheck,
  "check-circle": CheckCircle,
  "code": Code,
  "eye": Eye,
  "eye-off": EyeOff,
  "file-plus": FilePlus,
  "help-circle": HelpCircle,
  "history": History,
  "info": Info,
  "layers": Layers,
  "percent": Percent,
  "phone-incoming": PhoneIncoming,
  "phone-outgoing": PhoneOutgoing,
  "repeat": Repeat,
  "scan-line": ScanLine,
  "shield": Shield,
  "shield-alert": ShieldAlert,
  "shield-check": ShieldCheck,
  "trash-2": Trash2,
  "undo": Undo,
  "user-plus": UserPlus,
};

// Emoji-Aliase: erlauben <Icon name="📅" /> (z. B. für server-gelieferte Icons).
const EMOJI = {
  "🗑️": Trash2, "✏️": SquarePen, "✎": SquarePen, "✍️": SquarePen, "📅": Calendar,
  "🗓️": CalendarClock, "🕒": Clock, "⏱️": Clock, "⏰": Clock, "⏳": Hourglass,
  "👤": User, "🧑": User, "🔗": Link2, "📄": FileText, "📋": ClipboardList, "📝": FilePen,
  "📜": ScrollText, "👥": Users, "👫": Users, "👪": Users, "✉️": Mail, "📧": Mail,
  "📩": Mail, "📬": Mail, "📭": Mail, "📨": Mail, "📥": Inbox, "🎟️": Ticket, "🎫": Ticket,
  "💃": Music, "🎵": Music, "🎨": Palette, "👗": Shirt, "⚙️": Settings, "🔧": Wrench,
  "⬇️": Download, "🧾": FileText, "🏷️": Tag, "📊": BarChart3, "📈": LineChart, "📍": MapPin,
  "🎭": Drama, "🚫": Ban, "🔔": Bell, "🖨️": Printer, "💬": MessageCircle, "🏦": Landmark,
  "🏛️": Landmark, "🎁": Gift, "🪑": Armchair, "💺": Sofa, "🔁": RotateCcw, "🔄": RotateCcw,
  "🎬": Video, "📹": Video, "📎": Paperclip, "🔍": Search, "💶": Euro, "💳": CreditCard,
  "🎂": Cake, "📦": Package, "🔒": Lock, "🔐": Lock, "🔑": KeyRound, "🖼️": Image, "🌙": Moon,
  "🏖️": TreePalm, "➕": Plus, "➖": Minus, "🎤": Mic, "🏆": Trophy, "📰": Newspaper,
  "🧮": Calculator, "📁": Folder, "👑": Crown, "💾": Save, "📱": Smartphone, "🛒": ShoppingCart,
  "🔖": Bookmark, "🖥️": Monitor, "🧪": FlaskConical, "🔌": Plug, "💞": Heart, "🤝": Handshake,
  "🎯": Target, "📷": Camera, "🪄": Wand2, "☀️": Sun, "⭐": Star, "🌟": Star, "🎓": GraduationCap,
  "🏠": Home, "🏫": Landmark, "📺": Monitor, "📌": Pin, "🎌": Flag,
  "📤": Send, "🗄️": Archive, "🗂️": FolderOpen, "📁": Folder, "⚠️": TriangleAlert,
};

// Echtes Instagram-Logo (Kamera-Glyphe) im lucide-Linienstil –
// lucide führt keine Brand-Icons, daher hier selbst gezeichnet.
function InstagramGlyph({ size = 20, strokeWidth = 1.8, style, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      style={{ verticalAlign: "-0.18em", flexShrink: 0, ...style }}
      aria-hidden={rest["aria-label"] || rest.role ? undefined : "true"} {...rest}>
      <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" />
      <circle cx="12" cy="12" r="4.3" />
      <circle cx="17.2" cy="6.8" r="0.4" fill="currentColor" stroke="none" />
      <circle cx="17.2" cy="6.8" r="1.1" strokeWidth={strokeWidth * 0.75} />
    </svg>
  );
}

export default function Icon({ name, size = 20, strokeWidth = 1.8, style, ...rest }) {
  if (!name) return null;   // {bedingung && "x"} ergibt false/"" -> nichts
  const Cmp = MAP[name] || GEN_MAP[name] || EMOJI[name] || GEN_MAP[GEN_EMOJI[ohneFE0F(name)]];
  if (!Cmp) {
    // Unbekanntes Emoji (z. B. aus Altdaten): lieber nichts als ein buntes Bild.
    if (typeof name === "string" && EMOJI_RE.test(name)) return null;
    return <span style={style} {...rest}>{name}</span>; // echter Text bleibt Text
  }
  // aria-hidden nur, wenn der Aufrufer das Icon nicht selbst benannt hat
  return <Cmp size={size} strokeWidth={strokeWidth} style={{ verticalAlign: "-0.18em", flexShrink: 0, ...style }} aria-hidden={rest["aria-label"] || rest.role ? undefined : "true"} {...rest} />;
}
